import React, { useEffect, useState } from 'react';
import AdvertiserRow from './pacing-table-advertiser-row';
import { AdvertiserNode } from '../types/pacing.types';
import { CalendarArrowUp, Eye, EyeOff, Loader2, RefreshCcw } from 'lucide-react';
import { PacingViewApiResponse } from '../endpoints/pacing.types';
import { Button } from 'src/components/ui/button';
import CreateViewButton from './create-view-button';
import { useCreatePacingViewMutation, useUpdatePacingViewDatesMutation, useUpdatePacingViewMutation } from '../endpoints/pacing.endpoints';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { getMonthBounds } from '../utils/date-utils';

interface PacingTableProps {
  view?: PacingViewApiResponse;
  data: AdvertiserNode[];
  showVisibilityToggles?: boolean;
  isLoading?: boolean;
  isUpdating?: boolean;
  isCreated?: (view: PacingViewApiResponse) => void;
}

const PacingTable: React.FC<PacingTableProps> = ({
  view,
  data,
  showVisibilityToggles = false,
  isLoading = false,
  isUpdating = false,
  isCreated = () => {},
}) => {
  const [showAll, setShowAll] = useState<boolean>(false);

  const [createView, { isLoading: isCreatingView }] = useCreatePacingViewMutation();
  const [canUpdate, setCanUpdate] = useState<boolean>(false);
  const [updateView, { isLoading: isUpdatingView }] = useUpdatePacingViewMutation();
  const [updateViewDates, { isLoading: isUpdatingViewDates }] = useUpdatePacingViewDatesMutation();

  useEffect(() => {
    if (data.some((advertiser) => advertiser.campaigns.some((campaign) => campaign.line_items.some((lineItem) => lineItem.pacing_item_status === 'update')))) {
      setCanUpdate(true);
    } else {
      setCanUpdate(false);
    }
  }, [data]);
  // const { refetch: refetchPacingData, isLoading: isRefreshingPacingData, isFetching: isFetchingPacingData } = useGetPacingDataQuery({ viewId: view?.id ?? '', refresh: true });

  const handleCreateView = async(name: string) => {
    const view = await createView({ name });
    if ('data' in view) {
      isCreated(view.data as unknown as PacingViewApiResponse);
    }
  };

  const handleUpdateView = async() => {
    await updateView({ viewId: view?.id ?? '' });
  };

  const handleUpdateViewDates = async(date: string) => {
    let startDate = new Date();
    let endDate = new Date();

    switch (date) {
      case 'this_month':
        startDate = getMonthBounds(new Date()).first;
        endDate = getMonthBounds(new Date()).last;
        break;
      case 'next_month':
        startDate = getMonthBounds(new Date(), true).first;
        endDate = getMonthBounds(new Date(), true).last;
        break;
    }

    await updateViewDates({ viewId: view?.id ?? '', startDate: formatYMDLocal(startDate), endDate: formatYMDLocal(endDate) });
  };

  function formatYMDLocal(d: Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  return (
    <div className="h-[100%] overflow-y-auto bg-white rounded-lg shadow">
      <div className="flex items-center justify-start">
        <Button
          size='med'
          variant='ghost'
          onClick={() => setShowAll(!showAll)}
          className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201 ${showAll ? 'bg-gray-201' : ''}`}
        >
          {showAll ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {showAll ? 'View' : 'Hide'}
        </Button>
        <Button
          size='med'
          variant='ghost'
          onClick={() => handleUpdateView()}
          disabled={!canUpdate || isUpdatingView}
          className={`relative flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201`}
        >
          <RefreshCcw className={`h-4 w-4 ${isUpdatingView ? 'animate-spin' : ''}`} />
          Update
          {canUpdate && (
            <div
              className='absolute top-1 right-4 w-2 h-2 rounded-full mr-1 bg-orange-501'
            />
          )}
        </Button>
        {/* {isRefreshingPacingData || isFetchingPacingData ? 'Updating...' : 'Update'} */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size='med'
              variant='ghost'
              onClick={() => {}}
              className={`flex flex-col hover:bg-gray-201 rounded-none p-8 px-4 pb-8 text-sm gap-1 border-r border-gray-201`}
            >
              <CalendarArrowUp className={`h-4 w-4 ${isUpdatingViewDates ? 'animate-spin' : ''}`} />
              Set Month
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => handleUpdateViewDates('this_month')}>
              <span className='font-bold'>
                This Month
              </span>
              {` (${getMonthBounds(new Date()).first.toLocaleDateString('en-US')} - ${getMonthBounds(new Date()).last.toLocaleDateString('en-US')})`}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleUpdateViewDates('next_month')}>
              <span className='font-bold'>
                Next Month
              </span>
              {` (${getMonthBounds(new Date(), true).first.toLocaleDateString('en-US')} - ${getMonthBounds(new Date(), true).last.toLocaleDateString('en-US')})`}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>


        <CreateViewButton
          onCreateView={(name) => handleCreateView(name)}
          isCreatingView={isCreatingView}
        />
      </div>
      <table className="min-w-full divide-y">
        <thead className="border border-gray-201 border-t-0 border-l-0 border-r-0">
          <tr>
            <th className="w-[3%] min-w-[15px] border-r border-t border-gray-201" />
            <th className="max-w-1/4 px-6 py-3 text-left text-xs font-medium text-black uppercase border-b border-r border-t border-gray-201">
              Name
            </th>
            <th className="w-[10%] px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-r border-t border-gray-201">
              Budget
            </th>
            <th className="w-[150px] px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-r border-t border-gray-201">
              Daily Budget
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-r border-t border-gray-201">
              Spent
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b border-t border-gray-201">
              Dates
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {isLoading ? (
            <tr>
              <td
                colSpan={7}
                className="px-6 py-8 text-center"
              >
                <div className="flex flex-col items-center justify-center gap-2 h-[50vh]">
                  <Loader2 className="animate-spin text-gray-201 h-24 w-24" />
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                className="px-6 py-4 w-full text-center text-lg h-[50vh] items-center"
              >
                <div>
                  No data available
                </div>
                <div className='text-sm text-gray-500 flex text-center justify-center items-center'>
                  Select a view or
                  {'  '}
                  <div
                    className='mx-1'
                    onClick={() => {}}
                  >
                    refresh
                  </div>
                  {'  '}
                  to get started
                </div>

              </td>
            </tr>
          ) : (
            data.map(advertiser => (
              <AdvertiserRow
                key={advertiser.id}
                view={view}
                advertiser={advertiser}
                showAll={showAll}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PacingTable;
